import React, { useState, useEffect, useMemo } from 'react';
import NavBar from '../components/NavBar/Navigation';
import Footer from '../components/Footer/Footer';
import DirectoryBannerImage from '../assets/directory.png';
import './Directory.css';

const COL = {
  firstName: 'name_mkmk5dp',
  lastName: 'surname_mkmk1yg5',
  phone: 'phone_mkvx1y7m',
  email: 'email_address_mkmknsre',
  role: 'role_mkmk829j',
  department: 'lookup_mktgm4bx',
  section: 'board_relation_mktdq7e0',
  hideFromDirectory: 'boolean_mm6e5f5k',
};

const HIDDEN_NAMES = ['000000M - IT Stores'];

const EXCLUDED_TERMS = [
  'tree planting agent',
  'tree planting agents',
  'planting agent',
];

const MONDAY_BOARD_ID = process.env.REACT_APP_MONDAY_DIRECTORY_BOARD_ID || '1795462709';
const MONDAY_TOKEN = process.env.REACT_APP_MONDAY_TOKEN;
const MONDAY_API_URL = 'https://api.monday.com/v2';

const DEPT_ORDER = ["CEO's Office", 'Operations'];

const mondayFetch = async (query) => {
  const res = await fetch(MONDAY_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: MONDAY_TOKEN,
      'API-Version': '2024-01',
    },
    body: JSON.stringify({ query }),
  });

  const json = await res.json();

  if (!res.ok) {
    throw new Error(json?.errors?.[0]?.message || 'Monday request failed');
  }

  return json;
};

const buildQuery = (boardId) => `
  query {
    boards(ids: ${boardId}) {
      groups {
        id
        title
      }
      items_page(limit: 500) {
        items {
          id
          name
          group {
            id
            title
          }
          column_values(ids: [
            "${COL.firstName}",
            "${COL.lastName}",
            "${COL.phone}",
            "${COL.email}",
            "${COL.role}",
            "${COL.department}",
            "${COL.section}",
            "${COL.hideFromDirectory}"
          ]) {
            id
            type
            text
            value
            ... on BoardRelationValue {
              display_value
            }
            ... on MirrorValue {
              display_value
            }
            ... on DependencyValue {
              display_value
            }
            ... on CheckboxValue {
              checked
            }
          }
        }
      }
    }
  }
`;

const normalizeText = (value = '') =>
  String(value).replace(/\s+/g, ' ').trim();

const safeJsonParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const getColObject = (columnValues, id) =>
  columnValues.find((c) => c.id === id);

const getCol = (columnValues, id) => {
  const col = getColObject(columnValues, id);
  if (!col) return '';

  if (typeof col.display_value === 'string' && normalizeText(col.display_value)) {
    return normalizeText(col.display_value);
  }

  if (typeof col.text === 'string' && normalizeText(col.text)) {
    return normalizeText(col.text);
  }

  if (typeof col.value === 'string' && col.value) {
    const parsed = safeJsonParse(col.value);

    if (parsed) {
      if (typeof parsed.text === 'string' && normalizeText(parsed.text)) {
        return normalizeText(parsed.text);
      }

      if (typeof parsed.label === 'string' && normalizeText(parsed.label)) {
        return normalizeText(parsed.label);
      }

      if (Array.isArray(parsed.labels) && parsed.labels.length) {
        return normalizeText(parsed.labels.join(', '));
      }
    }
  }

  return '';
};

/* Checkbox columns: `checked` on CheckboxValue, {"checked":"true"} in value,
   or "v" in text depending on API version — handle all three. */
const getCheckbox = (columnValues, id) => {
  const col = getColObject(columnValues, id);
  if (!col) return false;

  if (typeof col.checked === 'boolean') return col.checked;
  if (typeof col.checked === 'string') {
    return col.checked.toLowerCase() === 'true';
  }

  if (typeof col.value === 'string' && col.value) {
    const parsed = safeJsonParse(col.value);
    if (parsed && parsed.checked !== undefined) {
      return (
        parsed.checked === true ||
        String(parsed.checked).toLowerCase() === 'true'
      );
    }
  }

  const text = normalizeText(col.text).toLowerCase();
  return text === 'v' || text === 'true' || text === 'yes' || text === '✓';
};

const getDisplayDepartment = (columnValues) => {
  const department = getCol(columnValues, COL.department);
  const section = getCol(columnValues, COL.section);
  return department || section || '';
};

const getInitials = (name) => {
  const parts = normalizeText(name).split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '?';
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
};

/* Project Green 2026 brand tints */
const AVATAR_PALETTES = [
  ['#d5e7e1', '#005145'],
  ['#cde6dc', '#006a53'],
  ['#fbe0e9', '#a63c63'],
  ['#f4f3c4', '#6a6900'],
  ['#fde6c9', '#95590a'],
  ['#dff0ea', '#013b33'],
];

const avatarColor = (name) => {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) {
    h = (h * 31 + name.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTES[h % AVATAR_PALETTES.length];
};

const normalizeNameForCompare = (name = '') =>
  normalizeText(name).toLowerCase();

const isCEO = (person) => {
  const role = normalizeText(person.role).toLowerCase();
  const name = normalizeNameForCompare(person.name);

  return (
    role === 'ceo' ||
    role.includes('chief executive officer') ||
    name === 'joseph cuschieri'
  );
};

const isBeverleyCosta = (person) =>
  normalizeNameForCompare(person.name) === 'beverley costa';

const isChiefRole = (role = '') => {
  const r = normalizeText(role).toLowerCase();

  return (
    r.includes('chief') ||
    r === 'ceo' ||
    r === 'coo' ||
    r === 'cfo' ||
    r === 'cio' ||
    r === 'cto' ||
    r.includes('chief executive officer') ||
    r.includes('chief operating officer') ||
    r.includes('chief operations officer') ||
    r.includes('chief financial officer') ||
    r.includes('chief information officer') ||
    r.includes('chief technology officer') ||
    r.includes('chief people officer')
  );
};

const isHeadRole = (role = '') => {
  const r = normalizeText(role).toLowerCase();
  return r.includes('head');
};

const containsExcludedTerm = (value = '') => {
  const text = normalizeText(value).toLowerCase();
  return EXCLUDED_TERMS.some((term) => text.includes(term));
};

const shouldExcludePerson = (person) => {
  const name = normalizeText(person.name);
  const role = normalizeText(person.role);
  const department = normalizeText(person.department);

  if (person.hideFromDirectory) return true;

  if (!name || name === '-') return true;
  if (HIDDEN_NAMES.includes(name.trim())) return true;
  if (!name.includes(' ')) return true;

  if (containsExcludedTerm(name) || containsExcludedTerm(role)) {
    return true;
  }

  if (!department && containsExcludedTerm(name)) {
    return true;
  }

  return false;
};

const getDeptRank = (dept) => {
  const idx = DEPT_ORDER.indexOf(dept);
  return idx === -1 ? 999 : idx;
};

const getRoleRank = (person) => {
  if (isCEO(person)) return 0;
  if (isBeverleyCosta(person)) return 1;
  if (isChiefRole(person.role)) return 2;
  if (isHeadRole(person.role)) return 3;
  return 4;
};

const sortPeople = (list) =>
  [...list].sort((a, b) => {
    if (isCEO(a)) return -1;
    if (isCEO(b)) return 1;

    if (isBeverleyCosta(a)) return -1;
    if (isBeverleyCosta(b)) return 1;

    const deptRankDiff = getDeptRank(a.department) - getDeptRank(b.department);
    if (deptRankDiff !== 0) return deptRankDiff;

    const roleRankDiff = getRoleRank(a) - getRoleRank(b);
    if (roleRankDiff !== 0) return roleRankDiff;

    const aIncomplete = !a.email && !a.phone;
    const bIncomplete = !b.email && !b.phone;
    if (aIncomplete && !bIncomplete) return 1;
    if (!aIncomplete && bIncomplete) return -1;

    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });

const Directory = () => {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeDept, setActiveDept] = useState('All');

  useEffect(() => {
    const fetchDirectory = async () => {
      try {
        setLoading(true);
        setError(null);

        const json = await mondayFetch(buildQuery(MONDAY_BOARD_ID));
        if (json.errors) throw new Error(json.errors[0]?.message || 'API error');

        const board = json?.data?.boards?.[0];
        if (!board) throw new Error('Board not found');

        const activeGroup = board.groups.find(
          (g) => normalizeText(g.title).toLowerCase() === 'active'
        );

        if (!activeGroup) {
          throw new Error('No "Active" group found on this board');
        }

        const parsed = board.items_page.items
          .filter((item) => item.group?.id === activeGroup.id)
          .map((item) => {
            const first = getCol(item.column_values, COL.firstName);
            const last = getCol(item.column_values, COL.lastName);
            const fullName =
              normalizeText([first, last].filter(Boolean).join(' ')) ||
              normalizeText(item.name);

            return {
              id: item.id,
              name: fullName,
              email: getCol(item.column_values, COL.email),
              phone: getCol(item.column_values, COL.phone),
              role: getCol(item.column_values, COL.role),
              department: getDisplayDepartment(item.column_values),
              hideFromDirectory: getCheckbox(
                item.column_values,
                COL.hideFromDirectory
              ),
            };
          })
          .filter((p) => !shouldExcludePerson(p));

        setPeople(sortPeople(parsed));
      } catch (err) {
        console.error('Directory fetch error:', err);
        setError(err.message || 'Failed to load directory');
      } finally {
        setLoading(false);
      }
    };

    fetchDirectory();
  }, []);

  const departments = useMemo(() => {
    const depts = [...new Set(people.map((p) => p.department).filter(Boolean))];

    depts.sort((a, b) => {
      const ai = DEPT_ORDER.indexOf(a);
      const bi = DEPT_ORDER.indexOf(b);

      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    });

    return ['All', ...depts];
  }, [people]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();

    return sortPeople(
      people.filter((p) => {
        const matchesDept = activeDept === 'All' || p.department === activeDept;
        const matchesSearch =
          !q ||
          p.name.toLowerCase().includes(q) ||
          (p.email || '').toLowerCase().includes(q) ||
          (p.phone || '').toLowerCase().includes(q) ||
          (p.department || '').toLowerCase().includes(q) ||
          (p.role || '').toLowerCase().includes(q);

        return matchesDept && matchesSearch;
      })
    );
  }, [people, search, activeDept]);

  const grouped = useMemo(() => {
    if (activeDept !== 'All') return null;

    const map = new Map();

    filtered.forEach((p) => {
      const key = p.department || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    });

    const cleanedEntries = [...map.entries()].filter(([dept, members]) => {
      if (dept !== 'Other') return true;
      return members.some((m) => normalizeText(m.department));
    });

    return new Map(
      cleanedEntries.sort((a, b) => {
        const aRank = getDeptRank(a[0]);
        const bRank = getDeptRank(b[0]);

        if (aRank !== bRank) return aRank - bRank;
        return a[0].localeCompare(b[0], undefined, { sensitivity: 'base' });
      })
    );
  }, [filtered, activeDept]);

  const PersonCard = ({ person }) => {
    const [bg, fg] = avatarColor(person.name);
    const incomplete = !person.email && !person.phone;
    const [copied, setCopied] = useState(null);

    const copy = async (text, field) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(field);
        setTimeout(() => setCopied(null), 1500);
      } catch (err) {
        console.error('Copy failed:', err);
      }
    };

    return (
      <div className="dir-card">
        <div className="dir-card-avatar" style={{ background: bg, color: fg }}>
          {getInitials(person.name)}
        </div>

        <div className="dir-card-body">
          <p className="dir-card-name">{person.name}</p>

          <div className="dir-card-meta">
            {person.role && (
              <div className="dir-card-info">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span>{person.role}</span>
              </div>
            )}

            {person.department && (
              <div className="dir-card-info">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" />
                  <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                </svg>
                <span>{person.department}</span>
              </div>
            )}
          </div>

          <div className="dir-card-meta dir-card-meta--contacts">
            {person.email && (
              <span
                className={`dir-card-link${copied === 'email' ? ' dir-card-link--copied' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => copy(person.email, 'email')}
                onKeyDown={(e) => e.key === 'Enter' && copy(person.email, 'email')}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <polyline points="2,4 12,13 22,4" />
                </svg>
                {copied === 'email' ? 'Copied!' : person.email}
              </span>
            )}

            {person.phone && (
              <span
                className={`dir-card-link${copied === 'phone' ? ' dir-card-link--copied' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => copy(person.phone, 'phone')}
                onKeyDown={(e) => e.key === 'Enter' && copy(person.phone, 'phone')}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.64A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                </svg>
                {copied === 'phone' ? 'Copied!' : person.phone}
              </span>
            )}

            {incomplete && <span className="dir-card-no-contact">No contact info</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="directory-page">
      <NavBar />

      <div
        className="directory-banner"
        style={{ backgroundImage: `url(${DirectoryBannerImage})` }}
      >
        <div className="directory-banner-overlay" />
        <div className="directory-banner-text">
          <h2 className="directory-banner-heading">Directory</h2>
          <p className="directory-banner-sub">Find People Across The Agency</p>
        </div>
      </div>

      <div className="directory-controls-wrapper">
        <div className="directory-controls">
          <div className="dir-search-wrap">
            <svg className="dir-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>

            <input
              className="dir-search"
              type="text"
              placeholder="Search by name, email or department…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            {search && (
              <button
                className="dir-search-clear"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>

          <div className="dir-filter-pills">
            {departments.map((dept) => (
              <button
                key={dept}
                className={`dir-pill ${activeDept === dept ? 'dir-pill--active' : ''}`}
                onClick={() => setActiveDept(dept)}
              >
                {dept}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="directory-page-content">
        {loading && (
          <div className="dir-status">
            <div className="dir-spinner" />
            <p>Loading directory…</p>
          </div>
        )}

        {error && !loading && (
          <div className="dir-status dir-status--error">
            <p>Couldn't load the directory — {error}</p>
            <p className="dir-status-hint">
              Check your API key and board ID in <code>.env</code>, and confirm your column IDs in the <code>COL</code> map at the top of <code>Directory.js</code>.
            </p>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="dir-status">
            <p>No people match your search.</p>
          </div>
        )}

        {!loading && !error && (
          <>
            {grouped && [...grouped.entries()].map(([dept, members]) => (
              <div key={dept} className="dir-dept-section">
                <h3 className="dir-dept-heading">
                  <span>{dept}</span>
                  <span className="dir-dept-count">{members.length}</span>
                </h3>

                <div className="dir-grid">
                  {members.map((person) => (
                    <PersonCard key={person.id} person={person} />
                  ))}
                </div>
              </div>
            ))}

            {!grouped && (
              <div className="dir-grid">
                {filtered.map((person) => (
                  <PersonCard key={person.id} person={person} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Directory;